import { Box, Tab, Tabs, Tooltip } from '@material-ui/core';
import {
  AppearanceIcon,
  Backdrop,
  BiographyIcon,
  CarsIcon,
  PawIcon,
  PropsIcon,
  ResumeTopFoldIcon,
  RulerIcon,
  SkillsIcon,
  SocialIcon,
} from '@room-match/shared-ui-components';
import { MemoizedErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import React, { Suspense, lazy, useMemo } from 'react';
import { useHistory, useParams } from 'react-router';
import { CONFIG } from 'shared/config';
import { ROUTES } from 'shared/constants/ROUTES';
import { useSetHeaderBack, useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import { useStyles } from './MyInfo.styles';

const Cars = lazy(() => import('./Cars/Cars'));
const Pets = lazy(() => import('./Pets/Pets'));
const AssetsProp = lazy(() => import('./AssetsProp/AssetsProp'));
const GeneralAppearance = lazy(() => import('./GeneralAppearance/GeneralAppearance'));
const SizeCard = lazy(() => import('shared/components/SizeCard/SizeCard'));
const Resume = lazy(() => import('./Resume/Resume'));
const Skills = lazy(() => import('./Skills/Skills'));
const Social = lazy(() => import('./Social/Social'));
const Biography = lazy(() => import('./Biography/Biography'));

const tabs = [
  {
    name: 'general_appearance',
    icon: <AppearanceIcon className="appearance" />,
    header: 'General Appearance',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <GeneralAppearance />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'size_card',
    icon: <RulerIcon className="ruler" />,
    header: 'Size Card',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <SizeCard onSave={() => {}} />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'resume',
    icon: <ResumeTopFoldIcon className="resume" />,
    header: 'Resume',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <Resume />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'biography',
    icon: <BiographyIcon className="biography" />,
    header: 'Biography',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <Biography />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'skills',
    icon: <SkillsIcon className="skills" />,
    header: 'Skills',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <Skills />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'social',
    icon: <SocialIcon className="social" />,
    header: 'Social',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <Social />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'cars',
    icon: <CarsIcon className="cars" />,
    header: 'Cars',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <Cars />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'pets',
    icon: <PawIcon className="paw" />,
    header: 'Pets',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <Pets />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: false,
  },
  {
    name: 'assets_props',
    icon: <PropsIcon className="props" />,
    header: 'Assets / Props',
    component: (
      <MemoizedErrorBoundary>
        <Suspense fallback={<Backdrop isLoading />}>
          <AssetsProp />
        </Suspense>
      </MemoizedErrorBoundary>
    ),
    disabled: true,
  },
  {
    name: 'family',
    icon: <PropsIcon className="props" />,
    header: 'Family',
    component: <div>Family</div>,
    disabled: true,
  },
];

const prodFilter = ['assets_props', 'family'];

const MyInfo = () => {
  useSetHeaderTitle(`My Info`);
  useSetHeaderBack(false);

  const classes = useStyles();
  const { tab } = useParams() as { tab: string };
  const history = useHistory();
  const getActiveTab = useMemo(() => {
    return tabs.find((_tab) => _tab.name === tab)?.component;
  }, [tab]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push(`${ROUTES.TALENT.MYINFO}/${newValue}`);
  };
  return (
    <Box component="div" className={classes.root}>
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleTabChange}
        className={classes.tabContainer}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs
          .filter((t) => (CONFIG.APP_ENV === 'production' ? prodFilter.indexOf(t.name) === -1 : true))
          .map((tab, index) =>
            tab.disabled ? (
              <Tooltip title={'Coming Soon'} placement="right">
                <span>
                  <Tab
                    icon={tab.icon}
                    className={classes.tabItem}
                    key={index}
                    label={tab.header}
                    disabled={tab.disabled}
                    value={tab.name}
                    style={{ textTransform: 'capitalize' }}
                  />
                </span>
              </Tooltip>
            ) : (
              <Tab
                icon={tab.icon}
                className={classes.tabItem}
                key={index}
                label={tab.header}
                disabled={tab.disabled}
                value={tab.name}
                style={{ textTransform: 'capitalize' }}
              />
            ),
          )}
      </Tabs>
      <Box className={classes.content}>{getActiveTab}</Box>
    </Box>
  );
};

export default MyInfo;
